<template>
  <v-container>
    <h1 class="text-h5 mb-4">Links de Pago 2</h1>
    <v-alert type="warning" outlined>
      Verificación de pago no disponible. Un enlace creado o una redirección no confirma un pago.
      Este flujo no está habilitado para conciliación de pagos.
    </v-alert>
    <v-alert v-if="!companies.length" type="info">No tiene empresas habilitadas para este flujo.</v-alert>
    <v-alert v-if="error" type="error" role="alert">{{ error }}</v-alert>
    <v-form ref="form" @submit.prevent="createLink">
      <v-select v-model="companyId" :items="companies" item-text="name" item-value="empresa_id"
        label="Empresa" :disabled="saving" :rules="[required]" outlined />
      <v-text-field v-model="amount" label="Importe" inputmode="decimal"
        :disabled="saving || !companyId" :rules="[validAmount]" outlined />
      <v-text-field v-model="concept" label="Concepto" :disabled="saving || !companyId"
        :rules="[required]" outlined />
      <v-btn type="submit" color="primary" :loading="saving" :disabled="!companyId || loading || saving">
        Crear enlace
      </v-btn>
    </v-form>
    <v-card v-if="created" class="mt-4" outlined>
      <v-card-title>Enlace creado — pago sin verificar</v-card-title>
      <v-card-text>{{ created.concept }} · {{ created.amount }} {{ created.currency }}</v-card-text>
      <v-card-actions>
        <v-btn @click="copyLink(created)">Copiar enlace</v-btn>
        <v-btn :href="safeUrl(created.payment_link)" target="_blank" rel="noopener noreferrer">Abrir enlace</v-btn>
      </v-card-actions>
    </v-card>
    <v-data-table class="mt-6" :headers="headers" :items="links" :loading="loading"
      item-key="id" hide-default-footer :items-per-page="25" no-data-text="No hay enlaces en esta página">
      <template v-slot:item.verification><span>Pago sin verificar</span></template>
      <template v-slot:item.actions="{ item }">
        <v-btn small text :disabled="!safeUrl(item.payment_link)" @click="copyLink(item)">Copiar</v-btn>
        <v-btn small text :disabled="!safeUrl(item.payment_link)" :href="safeUrl(item.payment_link)"
          target="_blank" rel="noopener noreferrer">Abrir</v-btn>
      </template>
    </v-data-table>
    <v-row class="mt-2" align="center">
      <v-col><v-btn :disabled="!companyId || loading || saving || offset === 0" @click="changePage(-25)">Anterior</v-btn></v-col>
      <v-col class="text-center">Página {{ offset / 25 + 1 }}</v-col>
      <v-col class="text-right"><v-btn :disabled="!companyId || loading || saving || links.length < 25" @click="changePage(25)">Siguiente</v-btn></v-col>
    </v-row>
  </v-container>
</template>

<script>
import { safeHostedUrl } from '../modules/hostedLinks2'

export default {
  name: 'HostedLinks2',
  data () {
    return {
      companyId: null, amount: '', concept: '', links: [], created: null,
      loading: false, saving: false, error: '', offset: 0, requestVersion: 0,
      headers: [
        { text: 'ID', value: 'id' }, { text: 'Concepto', value: 'concept' },
        { text: 'Importe', value: 'amount' }, { text: 'Moneda', value: 'currency' },
        { text: 'Fecha', value: 'created_at' }, { text: 'Verificación', value: 'verification' },
        { text: 'Enlace', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    companies () { return this.$store.state.hostedLinks2.companies }
  },
  watch: {
    companyId () {
      this.offset = 0
      this.created = null
      this.amount = ''
      this.concept = ''
      this.loadLinks()
    }
  },
  mounted () {
    if (this.companies.length === 1) this.companyId = this.companies[0].empresa_id
  },
  beforeDestroy () { this.requestVersion++ },
  methods: {
    safeUrl: safeHostedUrl,
    required (value) { return Boolean(String(value || '').trim()) || 'Este campo es obligatorio.' },
    validAmount (value) {
      return (/^\d{1,10}(?:\.\d{1,2})?$/.test(value) && Number(value) > 0) ||
        'Ingrese un importe positivo con punto decimal y hasta dos decimales.'
    },
    async loadLinks () {
      const version = ++this.requestVersion
      this.links = []
      this.error = ''
      this.loading = false
      if (!this.companyId) return
      this.loading = true
      try {
        const links = await this.$store.dispatch('hostedLinks2/listLinks', {
          empresa_id: this.companyId, offset: this.offset
        })
        if (version === this.requestVersion) this.links = links
      } catch (_) {
        if (version === this.requestVersion) this.error = 'No se pudo cargar el listado de enlaces.'
      } finally {
        if (version === this.requestVersion) this.loading = false
      }
    },
    changePage (delta) { this.offset += delta; this.loadLinks() },
    async createLink () {
      if (this.saving || !this.companyId || !this.$refs.form.validate()) return
      this.saving = true
      this.error = ''
      this.created = null
      try {
        this.created = await this.$store.dispatch('hostedLinks2/createLink', {
          empresa_id: this.companyId, amount: this.amount, concept: this.concept.trim()
        })
        this.offset = 0
        this.amount = ''
        this.concept = ''
        this.$refs.form.resetValidation()
        await this.loadLinks()
      } catch (_) {
        this.error = 'No se pudo confirmar la creación. No reintente automáticamente; puede existir un enlace sin registrar.'
      } finally { this.saving = false }
    },
    async copyLink (link) {
      const url = this.safeUrl(link.payment_link)
      if (!url) return
      try {
        await navigator.clipboard.writeText(url)
        this.$store.dispatch('show_snackbar', { text: 'Enlace copiado.', color: 'info' })
      } catch (_) { this.error = 'No se pudo copiar el enlace. Puede abrirlo con el botón Abrir.' }
    }
  }
}
</script>
